import {
  Box,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  DynamoDbBillingMode,
  IndexRestoreMode,
  Provider,
  RestoreMethod,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { Icon } from '@/components/shared/icon';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { AutomaticRestoreMethodSection } from './automaticRestoreMethodSection';
import { EonTagRemark } from './eonTagRemark';
import { SecondaryIndexesSection } from './secondaryIndexesSection';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreDynamoState } from '../restoreDynamoWizard';

export const DynamoConfiguration = (props: StepProps<RestoreDynamoState>) => {
  const dal = useDAL();
  const { isDemo } = useEnvironment();
  const { isEmployee } = useUser();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const { dynamoAdvancedRestoreFlowEnabled } = useFeatureFlags();

  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const showSkipImport =
    isEmployee && !isDemo && prefResponse?.value?.isDevMode;

  const { data: importViaS3Supported } =
    dal.inventory.snapshots.dynamoDb.useRestoreMethodAvailability(
      props.wizardState.snapshot.id,
      {
        restoreMethod: RestoreMethod.RESTORE_METHOD_IMPORT_TABLE,
        destination: {
          awsDynamodb: {
            restoreRegion: props.wizardState.regionName || '',
            encryptionKeyId: '',
            restoredName: '',
            advancedIndexConfig: {
              localSecondaryIndexMode:
                props.wizardState.includeLSI ||
                props.wizardState.includeAllSecondaryIndexes
                  ? IndexRestoreMode.INDEX_RESTORE_MODE_KEEP
                  : IndexRestoreMode.INDEX_RESTORE_MODE_DISCARD,
            },
          },
        },
      }
    );

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  const canGoNext = () => {
    if (
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.restoredName ||
      !getEncryptionKey(props.wizardState.encryption)
    ) {
      return false;
    }

    return true;
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = props.wizardState.tags || {};
    tags['eon:restore'] = 'true';

    const advancedIndexConfig = dynamoAdvancedRestoreFlowEnabled
      ? {
          localSecondaryIndexMode:
            props.wizardState.includeLSI ||
            props.wizardState.includeAllSecondaryIndexes
              ? IndexRestoreMode.INDEX_RESTORE_MODE_KEEP
              : IndexRestoreMode.INDEX_RESTORE_MODE_DISCARD,
          globalSecondaryIndexMode:
            props.wizardState.includeGSI ||
            props.wizardState.includeAllSecondaryIndexes
              ? IndexRestoreMode.INDEX_RESTORE_MODE_KEEP
              : IndexRestoreMode.INDEX_RESTORE_MODE_DISCARD,
        }
      : undefined;

    const restoreBillingMode = dynamoAdvancedRestoreFlowEnabled
      ? props.wizardState.writeMode === 'Provisioned'
        ? DynamoDbBillingMode.DYNAMODB_BILLING_MODE_PROVISIONED
        : DynamoDbBillingMode.DYNAMODB_BILLING_MODE_PAY_PER_REQUEST
      : undefined;

    return dal.restore
      .dynamoDB(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        restoreMethod: props.wizardState.restoreMethod,
        destination: {
          awsDynamodb: {
            tags,
            encryptionKeyId:
              getEncryptionKey(props.wizardState.encryption) || '',
            restoredName: props.wizardState.restoredName || '',
            restoreRegion: props.wizardState.regionName || '',
            writeCapacityUnits: props.wizardState.writeCapacityUnits,
            advancedIndexConfig,
            restoreBillingMode,
            skipActualImport: props.wizardState.skipActualImport,
          },
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.restoredName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore Dynamo table: ${errorMessage}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Table name</FormLabel>
            <TextField
              data-testid='databaseNameTextField'
              size='small'
              value={props.wizardState.restoredName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  restoredName: event.target.value,
                }))
              }
            />
          </FormControl>
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
              }))
            }
          />
        </Stack>
        {!dynamoAdvancedRestoreFlowEnabled && (
          <Stack
            direction='row'
            alignItems='center'
            gap='36px'
            className='mt-[20px]'
          >
            <FormControl size='small' className='flex-1'>
              <Stack direction='row' alignItems='center' className='mb-[8px]'>
                <FormLabel className='m-0'>Write capacity units</FormLabel>
                <Tooltip
                  title={
                    <>
                      {
                        'During the restore process, write capacity units are temporarily increased to accelerate restoration.'
                      }
                      <br />
                      {
                        'Once the process completes, the capacity and billing mode are reverted to match the original table configuration.'
                      }
                    </>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        padding: '16px',
                      },
                    },
                  }}
                >
                  <Icon iconClass='material-symbols-info-outline' />
                </Tooltip>
              </Stack>
              <TextField
                data-testid='databaseNameTextField'
                size='small'
                value={props.wizardState.writeCapacityUnits || ''}
                type='number'
                slotProps={{ htmlInput: { step: '1', min: '0' } }}
                variant='outlined'
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    writeCapacityUnits: Number(event.target.value),
                  }))
                }
              />
            </FormControl>
          </Stack>
        )}
      </Box>
      <Divider />
      <CardContent className='px-0'>
        {props.wizardState.regionName && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      <CardContent className='px-0 pb-[24px]'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
      {dynamoAdvancedRestoreFlowEnabled && (
        <Stack gap='12px'>
          {(props.wizardState.hasLSI || props.wizardState.hasGSI) && (
            <>
              <Divider />
              <CardContent className='px-0 py-[12px]'>
                <SecondaryIndexesSection
                  hasLSI={props.wizardState.hasLSI}
                  hasGSI={props.wizardState.hasGSI}
                  includeLSI={props.wizardState.includeLSI}
                  includeGSI={props.wizardState.includeGSI}
                  includeAllSecondaryIndexes={
                    props.wizardState.includeAllSecondaryIndexes
                  }
                  setIncludeLSI={(value) =>
                    props.setWizardState((state) => ({
                      ...state,
                      includeLSI: value,
                      restoreMethod: value
                        ? state.restoreMethod ===
                          RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
                          ? RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
                          : state.restoreMethod
                        : state.restoreMethod,
                    }))
                  }
                  setIncludeGSI={(value) =>
                    props.setWizardState((state) => ({
                      ...state,
                      includeGSI: value,
                    }))
                  }
                  setIncludeAllSecondaryIndexes={(value) =>
                    props.setWizardState((state) => ({
                      ...state,
                      includeAllSecondaryIndexes: value,
                      restoreMethod: value
                        ? state.restoreMethod ===
                          RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
                          ? RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
                          : state.restoreMethod
                        : state.restoreMethod,
                    }))
                  }
                />
              </CardContent>
            </>
          )}
          <Divider />
          <CardContent className='px-0 py-[12px]'>
            <AutomaticRestoreMethodSection
              restoreMethod={props.wizardState.restoreMethod}
              writeMode={props.wizardState.writeMode}
              setWriteMode={(value) =>
                props.setWizardState((state) => ({
                  ...state,
                  writeMode: value,
                }))
              }
              setRestoreMethod={(value) =>
                props.setWizardState((state) => ({
                  ...state,
                  restoreMethod: value,
                }))
              }
              disabledMethods={
                !importViaS3Supported?.available
                  ? [RestoreMethod.RESTORE_METHOD_IMPORT_TABLE]
                  : []
              }
              writeCapacityUnits={props.wizardState.writeCapacityUnits || 0}
              setWriteCapacityUnits={(value) =>
                props.setWizardState((state) => ({
                  ...state,
                  writeCapacityUnits: value,
                }))
              }
            />
          </CardContent>
          <Divider />
        </Stack>
      )}
      {showSkipImport && (
        <CardContent className='px-0 py-[12px]'>
          <Stack direction='row' alignItems='center' className='mx-[40px]'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.wizardState.skipActualImport}
                  onChange={(event) =>
                    props.setWizardState((state) => ({
                      ...state,
                      skipActualImport: event.target.checked,
                    }))
                  }
                />
              }
              label='Skip actual import (dev mode)'
            />
            <Tooltip title='Runs the full restore flow up to the actual import and returns there. Used for debugging.'>
              <Icon iconClass='material-symbols-info-outline' />
            </Tooltip>
          </Stack>
        </CardContent>
      )}
    </StepContainer>
  );
};
